import cloneDeep from 'lodash/cloneDeep';
import findDescriptorsForSubstanceShipment
    from 'pages/Evaluation/Product/ScenariosManagement/findDescriptorsConditionsForSubstanceShipment';
import { newLogoReachOk64 } from 'reports/base64Images';
import partialTableCEDEMPforProduct_LCS_PC from "reports/partialTableGenerators/CEDEMPforProduct_LCS_PC";
import { groupBy, mixtureSubstancesByMixture, mixtureSubstancesBySubstance } from 'selectors/generic';
import store from 'source/store';
import theme from 'source/theme';
import { ERC, PC, PROC, SU } from 'utils/constants/descriptorTypes';
import { shortDate } from 'utils/helperFunctions/dateFormat';

const lightGrey = '#EEEEEE';

// `product.product_raw_materials` - the composition rows of a product. Built at
// module level, not per call: createSelector keeps a single result.
const compositionByProductIndex = groupBy('productRawMaterials', 'product_id');

const rowGenerator_SU_PC_ERC_PROC = (lcs, productId, evaluationScenario) => {
    const state = cloneDeep(store.getState());

    const conditionsById = state.conditions.models;
    const descriptorsById = state.descriptors.models;
    const rawMaterialsById = state.rawMaterials.models;
    // The three joins findDescriptorsForSubstanceShipment used to read off the
    // product and the raw materials: the composition rows, and both directions
    // of mixture_substance.
    const compositionByProduct = compositionByProductIndex(state);
    const substancesByMixture = mixtureSubstancesByMixture(state);
    const mixturesBySubstance = mixtureSubstancesBySubstance(state);
    const scenarioDescriptors = state.scenarioDescriptors.data;
    const scenarioDescriptorConditions = state.scenarioDescriptorConditions.data;
    const scenariosData = state.scenarios.data;
    const shipmentsById = state.rawMaterialSuppliers.models;
    const suppliersById = state.suppliers.models;
    const unitsById = state.units.models;

    const { augmentedDescriptors: augmentedDescriptorsPC, augmentedConditionsByDescriptorId: PCNotRelevant } =
        findDescriptorsForSubstanceShipment(
            conditionsById, descriptorsById, PC, evaluationScenario, lcs, productId, compositionByProduct,
            rawMaterialsById, scenarioDescriptors, scenarioDescriptorConditions, scenariosData,
            shipmentsById, unitsById, substancesByMixture, mixturesBySubstance);

    const { augmentedDescriptors: augmentedDescriptorsSU, augmentedConditionsByDescriptorId: SUNotRelevant } =
        findDescriptorsForSubstanceShipment(
            conditionsById, descriptorsById, SU, evaluationScenario, lcs, productId, compositionByProduct,
            rawMaterialsById, scenarioDescriptors, scenarioDescriptorConditions, scenariosData,
            shipmentsById, unitsById, substancesByMixture, mixturesBySubstance);

    const {
        augmentedDescriptors: augmentedDescriptorsERC,
        augmentedConditionsByDescriptorId: augmentedConditionsByDescriptorIdERC
    } =
        findDescriptorsForSubstanceShipment(
            conditionsById, descriptorsById, ERC, evaluationScenario, lcs, productId, compositionByProduct,
            rawMaterialsById, scenarioDescriptors, scenarioDescriptorConditions, scenariosData,
            shipmentsById, unitsById, substancesByMixture, mixturesBySubstance);

    const {
        augmentedDescriptors: augmentedDescriptorsPROC,
        augmentedConditionsByDescriptorId: augmentedConditionsByDescriptorIdPROC
    } =
        findDescriptorsForSubstanceShipment(
            conditionsById, descriptorsById, PROC, evaluationScenario, lcs, productId, compositionByProduct,
            rawMaterialsById, scenarioDescriptors, scenarioDescriptorConditions, scenariosData,
            shipmentsById, unitsById, substancesByMixture, mixturesBySubstance);

    const rowGenerateDescriptorsWithConditions = (augmentedDescriptors, augmentedConditionsByDescriptorId) => {
        const rows = [];
        augmentedDescriptors.forEach(augDescriptor => {
            rows.push([
                { text: augDescriptor.code, fillColor: theme.palette.primary.light },
                { text: augDescriptor.description, colSpan: 7, fillColor: theme.palette.primary.light },
                {},
                {},
                {},
                {},
                {},
                {}
            ]);

            augDescriptor.substances.forEach(substance => {
                // rows.push([
                //   { text: '', border: [false, false, false, false] },
                //   { text: '', border: [false, false, false, false] },
                //   { text: substance.name, colSpan: 6 },
                //   {},
                //   {},
                //   {},
                //   {},
                //   {}
                // ]);

                augDescriptor.shipmentsBySubstanceId[substance.id].forEach(shipment => {
                    rows.push([
                        { text: '', border: [false, false, false, false], color: theme.palette.secondary.dark },
                        { text: '', border: [false, false, false, false], color: theme.palette.secondary.dark },
                        { text: '', border: [false, false, false, false], color: theme.palette.secondary.dark },
                        {
                            text: shipment.code,
                            border: [false, false, false, false],
                            color: theme.palette.secondary.dark
                        },
                        {
                            text: shipment.trade_name,
                            colSpan: 2,
                            border: [false, false, false, false],
                            color: theme.palette.secondary.dark
                        },
                        {},
                        {
                            text: suppliersById[shipment.supplier_id].name,
                            colSpan: 2,
                            border: [false, false, false, false],
                            color: theme.palette.secondary.dark
                        },
                        {}
                    ]);
                });
            });

            if (augmentedConditionsByDescriptorId && augmentedConditionsByDescriptorId[augDescriptor.id]) {
                Object.values(augmentedConditionsByDescriptorId[augDescriptor.id]).forEach(conditionInfo => {
                    // conditionInfo is grouped, so an empty group would take the
                    // whole PDF down on the first line it tried to print.
                    if (!conditionInfo.length) {
                        return;
                    }

                    rows.push([
                        { text: '', border: [false, false, false, false] },
                        { text: conditionInfo[0].name, colSpan: 7, fillColor: lightGrey },
                        {},
                        {},
                        {},
                        {},
                        {},
                        {}
                    ]);

                    conditionInfo[0].substances.forEach(substance => {
                        // rows.push([
                        //   { text: '', border: [false, false, false, false] },
                        //   { text: '', border: [false, false, false, false] },
                        //   { text: '', border: [false, false, false, false] },
                        //   { text: substance.name, colSpan: 5 },
                        //   {},
                        //   {},
                        //   {},
                        //   {}
                        // ]);

                        (conditionInfo[0].shipmentsBySubstanceId[substance.id] || []).forEach(shipment => {
                            rows.push([
                                { text: '', border: [false, false, false, false], color: theme.palette.secondary.dark },
                                { text: '', border: [false, false, false, false], color: theme.palette.secondary.dark },
                                {
                                    text: `${shipment.value ? shipment.value : ''} ${shipment.unit ? shipment.unit : ''}`,
                                    border: [false, false, false, false],
                                    color: theme.palette.secondary.dark
                                },
                                {
                                    text: shipment.code,
                                    border: [false, false, false, false],
                                    color: theme.palette.secondary.dark
                                },
                                {
                                    text: shipment.trade_name,
                                    colSpan: 2,
                                    border: [false, false, false, false],
                                    color: theme.palette.secondary.dark
                                },
                                {},
                                {
                                    text: suppliersById[shipment.supplier_id].name,
                                    colSpan: 2,
                                    border: [false, false, false, false],
                                    color: theme.palette.secondary.dark
                                },
                                {}
                            ]);
                        });
                    });
                });
            }
        });

        return rows;
    };

    return [
        ...rowGenerateDescriptorsWithConditions(augmentedDescriptorsPC),
        ...rowGenerateDescriptorsWithConditions(augmentedDescriptorsSU),
        ...rowGenerateDescriptorsWithConditions(augmentedDescriptorsERC, augmentedConditionsByDescriptorIdERC),
        ...rowGenerateDescriptorsWithConditions(augmentedDescriptorsPROC, augmentedConditionsByDescriptorIdPROC)
    ];
};

// For the new Filter
// ---------------------

// ---------------------

const dd = (lcs, product, evaluationScenario,
            // For CEDEMP the new filter
            allScenarios, shipmentsById, rawMaterialsById, lcsById
) => {

    return {
        pageSize: 'A4',
        pageMargins: [30, 60, 30, 40],

        footer: function (currentPage, pageCount) {
            return {
                text: `${shortDate(new Date())} - ${currentPage.toString()} de ${pageCount}`,
                alignment: 'right',
                relativePosition: { x: -15, y: 20 },
                fontSize: 7
            };
        },
        header: function (currentPage, pageCount, pageSize) {
            return [
                {
                    image: newLogoReachOk64,
                    width: 50,
                    relativePosition: { x: 20, y: 10 }
                }
            ];
        },
        content: [
            {
                style: 'header',
                text: `Cenário Avaliação Análise`
            },
            {
                style: 'header',
                text: `${product.code} - ${product.name}`
            },
            partialTableCEDEMPforProduct_LCS_PC(
                product, evaluationScenario, lcs, allScenarios, shipmentsById, lcsById, rawMaterialsById
            ),
            {
                style: 'tableExample',
                layout: 'lightHorizontalLines',
                table: {
                    headerRows: 0,
                    widths: [530],
                    body: [
                        [
                            { text: "CONCLUSÃO DE AVALIAÇÃO", colSpan: 1, fillColor: theme.palette.secondary.light }
                        ],
                        [
                            { text: evaluationScenario.notes, colSpan: 1 }
                        ]
                    ]
                }
            },
            {
                style: 'tableExample',
                layout: 'lightHorizontalLines',
                table: {
                    headerRows: 0,
                    widths: [30, 40, 60, 60, 60, 60, 60, 50],
                    body: [
                        [
                            { text: evaluationScenario.code, colSpan: 2, fillColor: theme.palette.primary.main },
                            {},
                            { text: lcs.description, colSpan: 2, fillColor: theme.palette.primary.main },
                            {},
                            { text: evaluationScenario.designation, colSpan: 4, fillColor: theme.palette.primary.main },
                            {},
                            {},
                            {}
                        ],
                        ...rowGenerator_SU_PC_ERC_PROC(lcs, product.id, evaluationScenario)
                    ]
                }
            }
        ],
        styles: {
            header: {
                color: theme.palette.primary.main,
                fontSize: 16,
                bold: true,
                alignment: 'center'
            },
            tableExample: {
                margin: [0, 15, 0, 0],
                fontSize: 8
            }
        }
    };
};

export default dd;
